// Server-side checks post suggestions with a bracketed prefix so they can be
// archived by prefix later: "[Policy check · <Title> § <n>] …" from
// services/agencyDocuments/policyCheck.ts and "[Footage check · <kind> ·
// <span>] …" from services/video/footageCheck.ts. The panel shows that prefix
// as badges under the text instead of inline. The raw content is untouched
// (it is what "Add to report" sends), only the display is split. A policy
// finding shows just where it comes from ("Manual § 340.5"), without a
// "Policy check" label; a footage finding keeps its "Footage check" pill.

import type { PolicyReference } from "../../db/schema";

export type SuggestionKind = "policy" | "footage";

export type SuggestionTag = {
  kind: SuggestionKind;
  // Footage: "Footage check", then the reference parts split on " · ".
  // Policy: the reference parts only.
  parts: string[];
};

export type ParsedSuggestion = { text: string; tag: SuggestionTag | null };

const PREFIX_RE = /^\[(Policy check|Footage check)((?:\s·\s[^\]]*)?)\]\s*/;

export function parseSuggestionTag(content: string): ParsedSuggestion {
  const m = PREFIX_RE.exec(content || "");
  if (!m) return { text: content, tag: null };
  const kind: SuggestionKind = m[1] === "Policy check" ? "policy" : "footage";
  const rest = m[2]
    .split(" · ")
    .map((p) => p.trim())
    .filter(Boolean);
  const parts = kind === "policy" ? rest : [m[1], ...rest];
  return { text: content.slice(m[0].length), tag: { kind, parts } };
}

// The policy check builds its reference as "<document title> § <section>" (or
// the heading when the section has no number); this is the same string, so a
// badge can be matched back to the section the case retrieved.
export function policyReferenceLabel(r: Pick<PolicyReference, "documentTitle" | "sectionNumber" | "heading">): string {
  return [r.documentTitle, r.sectionNumber ? `§ ${r.sectionNumber}` : r.heading].filter(Boolean).join(" ");
}

export function findPolicyReference(label: string, references: PolicyReference[] | undefined): PolicyReference | undefined {
  return references?.find((r) => policyReferenceLabel(r) === label);
}
